import { Injectable, computed, inject, signal } from '@angular/core';
import { AUTH } from '@core/constant/api.constants';
import { BaseService } from './base.service';

interface PermissionItem {
  permission_key: string;
  method: string;
  url: string;
}

interface ModulePermissions {
  module: string;
  permissions: PermissionItem[];
}

interface PermissionsResponse {
  data?: {
    by_module?: ModulePermissions[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly baseService = inject(BaseService);

  /** Central permission state — accessible from any component via inject(PermissionService). */
  private readonly _permissions = signal<string[]>([]);
  private readonly _modules = signal<string[]>([]);
  private readonly _loaded = signal(false);

  /** Read-only signals for consumers. */
  readonly permissions = this._permissions.asReadonly();
  readonly modules = this._modules.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  /** True once permissions have been fetched (regardless of result). */
  readonly isLoaded = computed(() => this._loaded());

  /** Fetch permissions from API and store in state. */
  async loadPermissions(): Promise<void> {
    try {
      const res = (await this.baseService.callGetApi(AUTH.PERMISSIONS)) as PermissionsResponse;

      const modules = res?.data?.by_module ?? [];
      const perms = modules.flatMap((m) => m.permissions.map((p) => p.permission_key));
      this._permissions.set(perms);
      this._modules.set(modules.map((m) => m.module));
    } catch {
      this._permissions.set([]);
    } finally {
      this._loaded.set(true);
    }
  }

  /** Check if the user has a specific permission. */
  hasPermission(permission: string): boolean {
    return this._permissions().includes(permission);
  }

  /** Check if the user has at least one of the given permissions. */
  hasAnyPermission(permissions: string[]): boolean {
    const current = this._permissions();
    return permissions.some((p) => current.includes(p));
  }

  /** Check if any of the given modules exist in the user's permissions. */
  hasAnyModule(modules: string[]): boolean {
    const current = this._modules();
    return modules.some((m) => m === '' || current.includes(m));
  }

  /** Clear state (e.g. on logout). */
  clear(): void {
    this._permissions.set([]);
    this._modules.set([]);
    this._loaded.set(false);
  }
}
